import { useLazyQuery, useQuery } from '@apollo/client'
import RefreshIcon from '@mui/icons-material/Refresh'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import React, { useCallback, useEffect, useState } from 'react'
import { makeStyles } from 'tss-react/mui'
import {
  MediaAccessUrlQuery,
  MediaAccessUrlQueryVariables,
  mediaQuery,
  mediaQueryVariables,
} from '../../generated/graphql'
import Medias from '../../schema/Medias'
import GraphqlError from '../GraphqlError'
import Loading from '../Loading'

/** Minimal media metadata needed for display — can come from a parent list query. */
export interface MediaInfo {
  filename: string
  mimeType: string
  name: string
}

interface Props {
  idMedia: string
  showPreview?: boolean
  /**
   * Pre-fetched media metadata from a parent list query (e.g. GET_MEDIAS).
   * When provided, the internal GET_MEDIA network request is skipped entirely.
   */
  mediaData?: MediaInfo
}

/** Refresh 30 s before the signed URL actually expires to avoid dead links. */
const URL_EXPIRY_BUFFER_MS = 30_000

function isUrlExpired(issued: number, expiration: number): boolean {
  return Date.now() > (issued + expiration) * 1000 - URL_EXPIRY_BUFFER_MS
}

const ACCESS_URL_DEBOUNCE_MS = 300

const useStyles = makeStyles()(() => {
  return {
    imageMediaPreview: {
      borderWidth: '2px',
      borderRadius: '2px',
      borderColor: '#EEEEEE',
      borderStyle: 'dashed',
      maxWidth: '100%',
      objectFit: 'contain',
    },
    urlErrorWrapper: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      opacity: 0.7,
    },
    nameWithLoadingUrl: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
    },
  }
})

export const MediaFieldView: React.FC<Props> = ({ idMedia, showPreview = false, mediaData }) => {
  const { classes } = useStyles()

  // Skip the network round-trip when the parent has already provided the metadata
  const { loading, error, data } = useQuery<mediaQuery, mediaQueryVariables>(Medias.GET_MEDIA, {
    variables: { id: idMedia },
    skip: !!mediaData,
  })

  // Lazy query lets us control precisely when the request fires and allows us to
  // attach an AbortController so the in-flight HTTP connection is released on unmount.
  const [executeAccessUrlQuery, { data: mediaUrlData, error: mediaUrlError, loading: mediaUrlLoading }] = useLazyQuery<
    MediaAccessUrlQuery,
    MediaAccessUrlQueryVariables
  >(Medias.GET_ACCESS_URL, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'none',
  })

  const retryUrl = useCallback(() => {
    if (idMedia) {
      executeAccessUrlQuery({ variables: { id: idMedia }, fetchPolicy: 'network-only' })
    }
  }, [idMedia, executeAccessUrlQuery])

  useEffect(() => {
    if (!idMedia) return

    // Each mount creates its own controller so the cleanup only aborts
    // the request that belongs to this specific component lifecycle.
    const controller = new AbortController()

    const timer = setTimeout(() => {
      executeAccessUrlQuery({
        variables: { id: idMedia },
        // Passing the signal through Apollo's context threads it into the
        // underlying fetch call. When controller.abort() fires, the browser
        // closes the TCP connection immediately, freeing it for other requests.
        context: { fetchOptions: { signal: controller.signal } },
      })
    }, ACCESS_URL_DEBOUNCE_MS)

    return () => {
      clearTimeout(timer)
      // Abort any in-flight request. Without this, rapid page flips leave
      // dozens of open connections and the server starts refusing new ones —
      // which browsers surface as "CORS failed" because no response headers
      // (including CORS headers) are ever received.
      controller.abort()
    }
  }, [idMedia, executeAccessUrlQuery])

  const [isPreviewLoading, setIsPreviewLoading] = useState<boolean>(true)

  // A missing media record is a real structural error — surface it
  if (error) {
    return <GraphqlError error={error} />
  }
  if (!mediaData && (loading || !data)) {
    return <Loading variant="inline" />
  }

  const resolved: MediaInfo = mediaData ?? { ...data.media }
  const { filename, mimeType, name } = resolved
  const mediaName = name + (name !== filename ? ` [${filename}]` : '')

  const urlEntry = mediaUrlData?.mediaAccessUrl
  const accessUrl = urlEntry?.url
  const urlExpired = urlEntry ? isUrlExpired(urlEntry.issued, urlEntry.expiration) : false

  // URL fetch failed or cached URL is about to/already expired — show name + retry
  if (mediaUrlError || urlExpired || (!mediaUrlLoading && !accessUrl)) {
    return (
      <span className={classes.urlErrorWrapper}>
        {mediaName}
        <Tooltip title="Retry fetching link">
          <IconButton size="small" onClick={retryUrl}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </span>
    )
  }

  // URL still loading — show the filename immediately so the row isn't just a spinner
  if (mediaUrlLoading || !accessUrl) {
    return (
      <span className={classes.nameWithLoadingUrl}>
        {mediaName}
        <Loading variant="inline" />
      </span>
    )
  }

  const isImage = mimeType.split('/')[0] === 'image'

  return (
    <span>
      {showPreview && isImage ? (
        <a href={accessUrl} rel="noreferrer noopener" target="_blank">
          {isPreviewLoading && <Loading variant="inline" />}
          <img
            alt={mediaName}
            className={classes.imageMediaPreview}
            onLoad={() => setIsPreviewLoading(false)}
            src={accessUrl}
          />
        </a>
      ) : (
        <a href={accessUrl} rel="noreferrer noopener" target="_blank">
          {mediaName}
        </a>
      )}
    </span>
  )
}
